import type { Field } from '@formily/core';
import { useField } from '@formily/react';
import { observer } from '@formily/reactive-react';
import { TreeSelect, type TreeSelectProps } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import { getFormContext } from '../../utils/formContext';
import { resolveTemplateValue } from '../../utils/data-parser';
import { transformToReferenceValue } from '../reference-select/utils';
import type { IReferenceValue } from '../reference-select/helper';
import type { IFormReferenceTreeSelectAsyncProps } from './helper';
import { useAsyncReferenceTree } from './useAsyncReferenceTree';

// Mirrors reference-select: strip the BE-enriched reference to the clean shape
// before submit. "Still enriched" is detected by markers the BE adds (never
// produced by transformToReferenceValue, so this can't loop).
const ENRICHED_REFERENCE_MARKERS = ['data', 'modelVersionId', 'updatedAt'];
const isEnrichedReference = (value: any): boolean =>
  !!value &&
  typeof value === 'object' &&
  ENRICHED_REFERENCE_MARKERS.some(key => key in value);
const shouldBuildInitialValue = (initialValue: any): boolean => {
  if (!initialValue) return false;
  if (Array.isArray(initialValue))
    return initialValue.some(isEnrichedReference);
  return isEnrichedReference(initialValue);
};

interface ILabelValue {
  value: string;
  label: React.ReactNode;
}

const FormReferenceTreeSelectAsync = observer(() => {
  const field = useField<Field>();
  const componentProps = (field.componentProps ||
    {}) as IFormReferenceTreeSelectAsyncProps;
  const {
    modelIds,
    modelInstanceFilters,
    rootFilter,
    transformer,
    childKey,
    parentKey,
    hasChildrenKey,
    searchableFields,
    selectableExpression,
    leavesOnly = false,
    numberToLoadOptions,
    placeholder,
    disabled: disabledProp,
    allowClear,
    mode,
    size,
    ...rest
  } = componentProps;

  const disabled = disabledProp || field.disabled;
  const isMultiple = mode === 'multiple' || mode === 'tags';
  const formContext = useMemo(() => getFormContext(field.form), [field.form]);

  const resolvedScopeFilter = useMemo(
    () =>
      modelInstanceFilters
        ? resolveTemplateValue(modelInstanceFilters, formContext, 'runtime')
        : undefined,
    [modelInstanceFilters, formContext]
  );
  const resolvedRootFilter = useMemo(
    () =>
      rootFilter
        ? resolveTemplateValue(rootFilter, formContext, 'runtime')
        : undefined,
    [rootFilter, formContext]
  );

  // Originally-loaded items, so removals in multi-select can be flagged
  // `toBeDeleted` instead of dropped (BE ignores omitted array items).
  const [originalItems] = useState<IReferenceValue[]>(() => {
    const initial = field.value ?? field.initialValue;
    return Array.isArray(initial) ? (initial as IReferenceValue[]) : [];
  });

  // Strip the enriched initial value; reactive since it can arrive after mount.
  useEffect(() => {
    if (shouldBuildInitialValue(field.initialValue)) {
      const transformed = transformToReferenceValue(
        field.initialValue,
        transformer
      );
      field.setInitialValue(transformed);
      field.setValue(transformed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.initialValue]);

  const {
    treeData,
    loadData,
    searchValue,
    setSearchValue,
    isLoading,
    getMeta,
  } = useAsyncReferenceTree({
    modelIds,
    scopeFilter: resolvedScopeFilter,
    rootFilter: resolvedRootFilter,
    transformer,
    childKey,
    parentKey,
    hasChildrenKey,
    selectableExpression,
    leavesOnly,
    isMultiple,
    pageSize: numberToLoadOptions,
    searchableFields,
  });

  // We use `labelInValue` so selected terms render correctly even when their
  // branch isn't loaded — the label rides along on the value.
  const selectValue = useMemo<ILabelValue | ILabelValue[] | undefined>(() => {
    const value = field.value;
    if (!value) return isMultiple ? [] : undefined;
    if (Array.isArray(value)) {
      return value
        .filter((v: IReferenceValue) => !v.toBeDeleted)
        .map((v: IReferenceValue) => ({
          value: v.id,
          label: v._formDisplayValue ?? v.id,
        }));
    }
    if (typeof value === 'object' && 'id' in value) {
      const v = value as IReferenceValue;
      return { value: v.id, label: v._formDisplayValue ?? v.id };
    }
    return undefined;
  }, [field.value, isMultiple]);

  const toReferenceValue = (
    id: string,
    label?: React.ReactNode
  ): IReferenceValue => {
    const meta = getMeta(id);
    const original = originalItems.find(o => o.id === id);
    return {
      id,
      modelId: meta?.modelId ?? original?.modelId,
      version: meta?.version ?? original?.version,
      _formDisplayValue:
        (typeof label === 'string' ? label : undefined) ??
        meta?.label ??
        original?._formDisplayValue ??
        id,
      _meta: meta?.meta ?? original?._meta,
    };
  };

  const handleChange = (value: ILabelValue | ILabelValue[] | undefined) => {
    if (isMultiple) {
      const selected = Array.isArray(value) ? value : [];
      const selectedIds = selected.map(v => v.value);
      const referenceValues = selected.map(v =>
        toReferenceValue(v.value, v.label)
      );

      originalItems.forEach(original => {
        if (!selectedIds.includes(original.id)) {
          referenceValues.push({ ...original, toBeDeleted: true });
        }
      });

      field.setValue(referenceValues);
      field.validate();
      return;
    }

    if (!value) {
      field.setValue(undefined);
      return;
    }
    const single = Array.isArray(value) ? value[0] : value;
    field.setValue(toReferenceValue(single.value, single.label));
    field.validate();
  };

  return (
    <TreeSelect
      {...rest}
      labelInValue
      value={selectValue as TreeSelectProps['value']}
      onChange={handleChange as TreeSelectProps['onChange']}
      treeData={treeData as TreeSelectProps['treeData']}
      loadData={loadData}
      showSearch
      searchValue={searchValue}
      onSearch={setSearchValue}
      filterTreeNode={false}
      placeholder={placeholder || 'Select...'}
      disabled={disabled}
      allowClear={allowClear}
      size={size}
      loading={isLoading}
      style={{ width: '100%' }}
      {...(isMultiple
        ? {
            treeCheckable: true,
            // Check nodes independently — ticking a parent must NOT cascade to
            // its children. Each submitted term is exactly what the user ticked.
            treeCheckStrictly: true,
          }
        : {})}
    />
  );
});

export default FormReferenceTreeSelectAsync;
